import { useState } from "react";

import { toFormData } from "@king-ict/eupisi-ui/utils";
import { useSelector, useDispatch } from "react-redux";

import {
  createFromExistingOtherParentDocument,
  createOtherParentDocument,
  deleteOtherParentDocument,
  getOtherParentDocument,
} from "../../../api/kindergartenApi";
import OtherParent from "../../../models/Person/OtherParent";
import DocumentMethodsReturnType, {
  DocumentDownloadRemoveMethodReturnType,
} from "../../../models/Request/DocumentMethodsReturnType";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import UploadDocument from "../../../models/Request/UploadDocument";
import {
  selectMainInfo,
  updateRequestOtherParentDocuments,
} from "../../../slices/requestSlice";

interface Props {
  setOtherParent: (prev: (prevData: OtherParent) => OtherParent) => void;
}

export function useOtherParentDocuments(): DocumentDownloadRemoveMethodReturnType;
export function useOtherParentDocuments(
  props: Props
): DocumentMethodsReturnType;
export function useOtherParentDocuments(props?: Props) {
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const requestMainInfoState = useSelector(selectMainInfo);
  const dispatch = useDispatch();
  const handleDownloadDocument = (document: RequestDocumentData) => {
    if (document.fileID) {
      getOtherParentDocument(document.fileID);
    }
  };

  const handleRemoveDocuments = (documents: RequestDocumentData[]) => {
    let ids = new Array<number>();
    documents.forEach((document) => {
      if (document.id) {
        ids.push(document.id);
      }
    });

    if (ids.length > 0) {
      deleteOtherParentDocument(ids);
    }
  };

  if (props) {
    const { setOtherParent } = props;

    const handleCloseDocumentsModal = (documents: RequestDocumentData[]) => {
      handleRemoveDocuments(documents);
      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleConfirmDocumentsModal = (documents: RequestDocumentData[]) => {
      setOtherParent((prev) => {
        const newDocuments = prev.documents
          ? [...prev.documents, ...documents]
          : [...documents];

        return { ...prev, documents: newDocuments };
      });

      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    const handleUploadDocuments = async (files: File[]) => {
      var documents = await createOtherParentDocument(
        toFormData<UploadDocument>({
          requestUID: requestMainInfoState.uId,
          applicantParentOib: requestMainInfoState.applicantParentOib,
          childOib: requestMainInfoState.childOib,
          files: files,
        })
      );
      return documents;
    };

    const handleRemoveDocument = (document: RequestDocumentData) => {
      if (document.id) {
        const ids = [document.id];
        deleteOtherParentDocument(ids);
      }
    };

    const handleRemoveDocumentFromList = (document: RequestDocumentData) => {
      setOtherParent((prev) => {
        if (prev.documents) {
          const newDocuments = prev.documents.filter(
            (d) => d.id !== document.id
          );

          dispatch(updateRequestOtherParentDocuments(newDocuments));

          return { ...prev, documents: newDocuments };
        }
        return prev;
      });

      handleRemoveDocument(document);
    };

    const handleUploadExistingDocument = async (
      document: RequestDocumentData
    ) => {
      var docResult = await createFromExistingOtherParentDocument(
        document.fileID!,
        document.origin
      );
      return docResult;
    };

    const handleToggleDocumentsModal = () => {
      setIsDocumentsModalOpen((isDocumentsModalOpen) => !isDocumentsModalOpen);
    };

    return {
      isDocumentsModalOpen,
      handleUploadDocuments,
      handleUploadExistingDocument,
      handleConfirmDocumentsModal,
      handleCloseDocumentsModal,
      handleToggleDocumentsModal,
      handleDownloadDocument,
      handleRemoveDocumentFromList,
      handleRemoveDocument,
      handleRemoveDocuments,
    };
  } else {
    return {
      handleDownloadDocument,
      handleRemoveDocuments,
    };
  }
}
